import React, { useState, useEffect, useRef, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  MagnifyingGlassIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'

import { apiClient, Opportunity } from '../../lib/api'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import ConfirmModal from '../../components/ui/ConfirmModal'
import RescheduleModal from '../../components/ui/RescheduleModal'
import { PromptPreview, type PromptPreviewData } from '../../components/PromptPreview'
import { EngagementMetrics } from '../../components/engagement/EngagementMetrics'
import { useCommentEngagement } from '../../hooks/useEngagement'

// Development mode flag
const isDevelopment = import.meta.env.DEV

// CRITICAL FIX: Safely extract keyword strings from complex objects
// Backend might return objects with {keyword, type, weight...} instead of strings
const safeExtractKeywords = (keywords: any): string[] => {
  if (!keywords) return []
  if (!Array.isArray(keywords)) return []

  return keywords.map((item: any) => {
    // If it's an object with a 'keyword' property, extract it
    if (typeof item === 'object' && item !== null && 'keyword' in item) {
      return String(item.keyword)
    }
    // Otherwise, convert to string
    return String(item)
  })
}

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Engagement Metrics Section Component
const EngagementMetricsSection: React.FC<{ commentId: string }> = ({ commentId }) => {
  const { data: engagementData, isLoading } = useCommentEngagement(Number(commentId))

  return (
    <div className="mt-6 pt-4 border-t border-gray-200">
      <h5 className="text-sm font-medium text-gray-700 mb-3">📊 Comment Engagement Metrics:</h5>
      <EngagementMetrics data={engagementData} loading={isLoading} />
    </div>
  )
}

const OpportunitiesPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(25)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [aiStatusFilter, setAIStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [projectFilter, setProjectFilter] = useState('')
  const [keywordFilter, setKeywordFilter] = useState('')
  const [selectedOpportunities, setSelectedOpportunities] = useState<string[]>([])
  const [isAIModalOpen, setIsAIModalOpen] = useState(false)
  const [selectedOpportunityForAI, setSelectedOpportunityForAI] = useState<Opportunity | null>(null)
  const [aiTone, setAITone] = useState('professional')
  const [aiStyle, setAIStyle] = useState('helpful')
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [generatedResponse, setGeneratedResponse] = useState('')
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false)
  const [templateUsed, setTemplateUsed] = useState<{ id: number; name: string; category: string } | null>(null)

  // Prompt preview states
  const [showPromptPreview, setShowPromptPreview] = useState(false)
  const [enablePromptPreview, setEnablePromptPreview] = useState(false)
  const [promptPreviewData, setPromptPreviewData] = useState<PromptPreviewData | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [customContext, setCustomContext] = useState('')

  // Scheduling states
  const [showSchedulingOptions, setShowSchedulingOptions] = useState(false)
  const [schedulingStrategy, setSchedulingStrategy] = useState<'optimal' | 'immediate' | 'custom'>('optimal')
  const [customScheduleDate, setCustomScheduleDate] = useState('')
  const [customScheduleTime, setCustomScheduleTime] = useState('')
  const [schedulePreview, setSchedulePreview] = useState<any>(null)
  const [isScheduling, setIsScheduling] = useState(false)
  const [generatedCommentId, setGeneratedCommentId] = useState<number | null>(null)

  // Row expansion states
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [currentTime, setCurrentTime] = useState(new Date())

  // Modal states
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false)
  const [postNowModalOpen, setPostNowModalOpen] = useState(false)
  const [selectedSchedule, setSelectedSchedule] = useState<{
    id: number
    scheduledTime: string
    opportunityTitle: string
    opportunity?: Opportunity
  } | null>(null)

  const queryClient = useQueryClient()

  // Ref to track pagination section
  const paginationRef = useRef<HTMLDivElement>(null)

  // Debounce search query to avoid API calls on every keystroke
  const debouncedSearchQuery = useDebounce(searchQuery, 500) // Wait 500ms after user stops typing

  // Debug logging
  if (isDevelopment) console.log('🔍 SEARCH DEBUG - OpportunitiesPage state:', {
    currentPage,
    perPage,
    searchQuery,
    debouncedSearchQuery,
    statusFilter,
    priorityFilter,
    projectFilter,
    keywordFilter
  })

  // Fetch available response templates
  const { data: templatesData } = useQuery({
    queryKey: ['response-templates'],
    queryFn: () => apiClient.getResponseTemplates(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Fetch opportunities with filters
  const { data: opportunitiesData, isLoading, error} = useQuery({
    queryKey: ['opportunities', currentPage, perPage, debouncedSearchQuery, statusFilter, aiStatusFilter, priorityFilter, projectFilter, keywordFilter],
    queryFn: async () => {
      if (isDevelopment) console.log('🚀 PAGINATION DEBUG - Making API call with params:', {
        page: currentPage,
        per_page: perPage,
        search: debouncedSearchQuery || undefined,
        status: statusFilter || undefined,
        filter_ai_status: aiStatusFilter || undefined,
        priority: priorityFilter || undefined,
        project_id: projectFilter || undefined,
        filter_keyword: keywordFilter || undefined,
      })

      if (isDevelopment) console.log('🔍 MAIN TABLE API CALL - Sending parameters:', {
        page: currentPage,
        per_page: perPage,
        search: debouncedSearchQuery || undefined,
        status: statusFilter || undefined,
        filter_ai_status: aiStatusFilter || undefined,
        priority: priorityFilter || undefined,
        project_id: projectFilter || undefined,
        filter_keyword: keywordFilter || undefined,
      })

      const result = await apiClient.getOpportunities({
        page: currentPage,
        per_page: perPage,
        search: debouncedSearchQuery || undefined,
        status: statusFilter || undefined,
        filter_ai_status: aiStatusFilter || undefined,
        priority: priorityFilter || undefined,
        project_id: projectFilter || undefined,
        filter_keyword: keywordFilter || undefined,
      })

      if (isDevelopment) console.log('🔍 MAIN TABLE API RESPONSE:', {
        opportunitiesCount: result.opportunities?.length || 0,
        pagination: result.pagination,
        sampleOpportunity: result.opportunities?.[0]
      })

      if (isDevelopment) console.log('✅ PAGINATION DEBUG - API response received:', {
        opportunitiesCount: result.opportunities?.length || 0,
        pagination: result.pagination,
        totalCount: result.pagination?.total_count,
        currentPageFromAPI: result.pagination?.page,
        perPageFromAPI: result.pagination?.per_page,
        totalPages: result.pagination?.total_pages
      })

      return result
    },
    staleTime: 30000,
  })

  // Fetch projects for filter dropdown
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiClient.getProjects(),
  })

  // Fetch breakdown metrics with separate API calls
  // Get count of "new" opportunities with current filters (except status)
  const { data: newOpportunitiesData } = useQuery({
    queryKey: ['new-opportunities-count', debouncedSearchQuery, aiStatusFilter, priorityFilter, projectFilter, keywordFilter],
    queryFn: async () => {
      return await apiClient.getOpportunities({
        page: 1,
        per_page: 1, // We only need the count, not the data
        search: debouncedSearchQuery || undefined,
        filter_ai_status: aiStatusFilter || undefined,
        priority: priorityFilter || undefined,
        project_id: projectFilter || undefined,
        filter_keyword: keywordFilter || undefined,
        status: 'new', // Always filter by "new" status for this metric
      })
    },
    staleTime: 30000,
  })

  // Get count of "high priority" opportunities with current filters (except priority)
  const { data: highPriorityData } = useQuery({
    queryKey: ['high-priority-count', debouncedSearchQuery, statusFilter, aiStatusFilter, projectFilter, keywordFilter],
    queryFn: async () => {
      return await apiClient.getOpportunities({
        page: 1,
        per_page: 1, // We only need the count, not the data
        search: debouncedSearchQuery || undefined,
        status: statusFilter || undefined,
        filter_ai_status: aiStatusFilter || undefined,
        project_id: projectFilter || undefined,
        filter_keyword: keywordFilter || undefined,
        priority: 'high', // Always filter by "high" priority for this metric
      })
    },
    staleTime: 30000,
  })

  // Get count of "contacted" opportunities (conditional based on user's status filter)
  const { data: contactedData } = useQuery({
    queryKey: ['contacted-count', debouncedSearchQuery, statusFilter, aiStatusFilter, priorityFilter, projectFilter, keywordFilter],
    queryFn: async () => {
      // If user filtered by a different status than "contacted", return 0
      if (statusFilter && statusFilter !== 'contacted') {
        return { pagination: { total_count: 0 } }
      }

      return await apiClient.getOpportunities({
        page: 1,
        per_page: 1, // We only need the count, not the data
        search: debouncedSearchQuery || undefined,
        filter_ai_status: aiStatusFilter || undefined,
        priority: priorityFilter || undefined,
        project_id: projectFilter || undefined,
        filter_keyword: keywordFilter || undefined,
        status: 'contacted', // Always filter by "contacted" status for this metric
      })
    },
    staleTime: 30000,
  })

  // Get count of "converted" opportunities (conditional based on user's status filter)
  const { data: convertedData } = useQuery({
    queryKey: ['converted-count', debouncedSearchQuery, statusFilter, aiStatusFilter, priorityFilter, projectFilter, keywordFilter],
    queryFn: async () => {
      // If user filtered by a different status than "converted", return 0
      if (statusFilter && statusFilter !== 'converted') {
        return { pagination: { total_count: 0 } }
      }

      return await apiClient.getOpportunities({
        page: 1,
        per_page: 1, // We only need the count, not the data
        search: debouncedSearchQuery || undefined,
        filter_ai_status: aiStatusFilter || undefined,
        priority: priorityFilter || undefined,
        project_id: projectFilter || undefined,
        filter_keyword: keywordFilter || undefined,
        status: 'converted', // Always filter by "converted" status for this metric
      })
    },
    staleTime: 30000,
  })

  // Get count of opportunities with AI draft responses (conditional based on user's AI filter)
  const { data: aiDraftData } = useQuery({
    queryKey: ['ai-draft-count', debouncedSearchQuery, statusFilter, aiStatusFilter, priorityFilter, projectFilter, keywordFilter],
    queryFn: async () => {
      return await apiClient.getOpportunities({
        page: 1,
        per_page: 1,
        search: debouncedSearchQuery || undefined,
        status: statusFilter || undefined, // Apply user's status filter
        filter_ai_status: 'draft', // Get draft count
        priority: priorityFilter || undefined,
        project_id: projectFilter || undefined,
        filter_keyword: keywordFilter || undefined,
      })
    },
    enabled: !(aiStatusFilter === 'no_comment' || aiStatusFilter === 'posted'), // Disable query when should return 0
    staleTime: 30000,
  })

  // Get count of opportunities with AI posted responses (conditional based on user's AI filter)
  const { data: aiPostedData } = useQuery({
    queryKey: ['ai-posted-count', debouncedSearchQuery, statusFilter, aiStatusFilter, priorityFilter, projectFilter, keywordFilter],
    queryFn: async () => {
      return await apiClient.getOpportunities({
        page: 1,
        per_page: 1, // We only need the count, not the data
        search: debouncedSearchQuery || undefined,
        status: statusFilter || undefined, // Apply user's status filter
        filter_ai_status: 'posted', // Get posted count
        priority: priorityFilter || undefined,
        project_id: projectFilter || undefined,
        filter_keyword: keywordFilter || undefined,
      })
    },
    enabled: !(aiStatusFilter === 'no_comment' || aiStatusFilter === 'draft'), // Disable query when should return 0
    staleTime: 30000,
  })

  // Extract unique keywords from all projects for the keyword filter dropdown
  const allKeywords = useMemo(() => {
    if (!Array.isArray(projects)) return []

    const keywordSet = new Set<string>()

    projects.forEach((project) => {
      // Extract keywords from project - they may be strings or objects with 'keyword' property
      const projectKeywords = safeExtractKeywords(project.keywords)
      projectKeywords.forEach((keyword) => {
        if (keyword && keyword.trim()) {
          keywordSet.add(keyword.trim())
        }
      })
    })

    // Sort alphabetically and return as array
    return Array.from(keywordSet).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
  }, [projects])

  // Use separate API calls for each metric to get accurate counts
  const opportunityMetrics = React.useMemo(() => {
    if (isDevelopment) console.log('📊 METRICS DEBUG - Combining data from multiple API calls:', {
      mainAPIPagination: opportunitiesData?.pagination?.total_count,
      newCount: newOpportunitiesData?.pagination?.total_count,
      highPriorityCount: highPriorityData?.pagination?.total_count,
      contactedCount: contactedData?.pagination?.total_count,
      convertedCount: convertedData?.pagination?.total_count,
      aiDraftCount: aiDraftData?.pagination?.total_count,
      aiPostedCount: aiPostedData?.pagination?.total_count,
      currentFilters: { statusFilter, aiStatusFilter, priorityFilter, projectFilter, debouncedSearchQuery }
    })

    if (isDevelopment) console.log('🔍 AI GENERATED DEBUG - Query details:', {
      statusFilter,
      aiStatusFilter,
      shouldSkipDraftQuery: aiStatusFilter === 'no_comment' || aiStatusFilter === 'posted',
      isDraftQueryEnabled: !(aiStatusFilter === 'no_comment' || aiStatusFilter === 'posted'),
      aiDraftQueryResult: aiDraftData,
      aiDraftCount: aiDraftData?.pagination?.total_count
    })

    // Use the main API's total_count for the "Total" metric
    // This is the same source that powers the "Showing 1 to 25 of 105 opportunities" text
    const totalFromMainAPI = opportunitiesData?.pagination?.total_count || 0

    if (isDevelopment) console.log('📊 METRICS DEBUG - Final metric values:', {
      total: totalFromMainAPI,
      new: newOpportunitiesData?.pagination?.total_count || 0,
      high_priority: highPriorityData?.pagination?.total_count || 0,
      contacted: contactedData?.pagination?.total_count || 0,
      converted: convertedData?.pagination?.total_count || 0,
      ai_generated: (aiStatusFilter === 'no_comment' || aiStatusFilter === 'posted') ? 0 : (aiDraftData?.pagination?.total_count || 0), // Only draft responses
      ai_posted: (aiStatusFilter === 'no_comment' || aiStatusFilter === 'draft') ? 0 : (aiPostedData?.pagination?.total_count || 0),
    })

    return {
      total: totalFromMainAPI,
      new: newOpportunitiesData?.pagination?.total_count || 0,
      high_priority: highPriorityData?.pagination?.total_count || 0,
      contacted: contactedData?.pagination?.total_count || 0,
      converted: convertedData?.pagination?.total_count || 0,
      ai_generated: (aiStatusFilter === 'no_comment' || aiStatusFilter === 'posted') ? 0 : (aiDraftData?.pagination?.total_count || 0), // Only draft responses
      ai_posted: (aiStatusFilter === 'no_comment' || aiStatusFilter === 'draft') ? 0 : (aiPostedData?.pagination?.total_count || 0),
    }
  }, [opportunitiesData, newOpportunitiesData, highPriorityData, contactedData, convertedData, aiDraftData, aiPostedData, statusFilter, aiStatusFilter, priorityFilter, projectFilter, debouncedSearchQuery])

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async (params: { ids: string[]; action: string }) => {
      // Use the WordPress bulk endpoint
      const { ids, action } = params
      return await apiClient.request('/wp-json/redd-miner-leads/v1/opportunities/bulk', {
        method: 'POST',
        body: JSON.stringify({ ids, action }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      setSelectedOpportunities([])
      toast.success('Opportunities updated successfully!')
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update opportunities')
    },
  })

  const opportunities = opportunitiesData?.opportunities || []
  const pagination = opportunitiesData?.pagination

  // Debug pagination data
  if (isDevelopment) console.log('📊 PAGINATION DEBUG - Render data:', {
    opportunitiesCount: opportunities.length,
    pagination: pagination,
    showPagination: pagination && pagination.total_pages > 1,
    paginationExists: !!pagination
  })

  // Handle search - only update the input value, debouncing will handle the API call
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    // Don't reset page here - let the useEffect handle it when debounced value changes
  }

  // Reset to first page when debounced search query changes (but not on initial load)
  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    setCurrentPage(1)
  }, [debouncedSearchQuery])

  // Handle filter changes
  const handleFilterChange = (filterType: string, value: string) => {
    setCurrentPage(1) // Reset to first page on filter change
    switch (filterType) {
      case 'status':
        setStatusFilter(value)
        break
      case 'ai_status':
        setAIStatusFilter(value)
        break
      case 'priority':
        setPriorityFilter(value)
        break
      case 'project':
        setProjectFilter(value)
        break
      case 'keyword':
        setKeywordFilter(value)
        break
    }
  }

  // Clear all filters and search
  const handleClearAllFilters = () => {
    setSearchQuery('')
    setStatusFilter('')
    setAIStatusFilter('')
    setKeywordFilter('')
    setPriorityFilter('')
    setProjectFilter('')
    setCurrentPage(1)
  }

  // Check if any filters are active
  const hasActiveFilters = searchQuery || statusFilter || aiStatusFilter || priorityFilter || projectFilter || keywordFilter

  // Function to maintain scroll position at pagination
  const scrollToPagination = () => {
    if (paginationRef.current) {
      const paginationTop = paginationRef.current.offsetTop - 100; // 100px offset from top
      window.scrollTo({
        top: paginationTop,
        behavior: 'smooth'
      });
      if (isDevelopment) console.log('📍 SCROLL DEBUG - Scrolled to pagination position:', paginationTop)
    }
  }

  // Handle per-page change
  const handlePerPageChange = (newPerPage: number) => {
    if (isDevelopment) console.log('🔄 PAGINATION DEBUG - Per-page changed from', perPage, 'to', newPerPage)
    setPerPage(newPerPage)
    setCurrentPage(1) // Reset to first page when changing per-page
    if (isDevelopment) console.log('🔄 PAGINATION DEBUG - Reset to page 1, new perPage will be:', newPerPage)

    // Maintain scroll position after state update
    setTimeout(scrollToPagination, 100)
  }

  // Handle opportunity selection
  const handleOpportunitySelect = (opportunityId: string) => {
    setSelectedOpportunities(prev =>
      prev.includes(opportunityId)
        ? prev.filter(id => id !== opportunityId)
        : [...prev, opportunityId]
    )
  }

  // Handle select all
  const handleSelectAll = () => {
    if (selectedOpportunities.length === opportunities.length) {
      setSelectedOpportunities([])
    } else {
      setSelectedOpportunities(opportunities.map(opp => opp.id))
    }
  }

  // Bulk actions
  const handleBulkAction = (action: string) => {
    if (selectedOpportunities.length === 0) {
      toast.error('Please select opportunities first')
      return
    }

    // Map frontend action names to backend action names
    let backendAction: string

    switch (action) {
      case 'mark-reviewed':
        backendAction = 'mark_reviewed'
        break
      case 'mark-contacted':
        backendAction = 'mark_contacted'
        break
      case 'mark-converted':
        backendAction = 'mark_converted'
        break
      case 'mark-archived':
        backendAction = 'mark_archived'
        break
      default:
        return
    }

    bulkUpdateMutation.mutate({ ids: selectedOpportunities, action: backendAction })
  }

  // Export to CSV
  const handleExportCSV = () => {
    const csvData = opportunities.map(opp => ({
      ID: opp.id,
      Title: opp.title,
      Subreddit: opp.subreddit,
      Author: opp.author,
      Priority: opp.priority,
      Status: opp.status,
      'AI Status': opp.ai_response?.status || 'no_comment',
      'Post Score': opp.post_score || 0,
      'Comments': opp.num_comments || 0,
      'Opportunity Score': opp.opportunity_score,
      'Keywords': opp.keyword_matches?.join(', ') || '',
      'Project': opp.project_name,
      'Discovered': opp.discovered_at,
      'Post URL': opp.post_url
    }))

    // Convert to CSV format
    const headers = Object.keys(csvData[0]).join(',')
    const rows = csvData.map(row =>
      Object.values(row).map(value =>
        typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value
      ).join(',')
    ).join('\n')

    const csvContent = `${headers}\n${rows}`

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `opportunities-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast.success('Opportunities exported to CSV!')
  }

  // AI Response Generation
  const handleOpenAIModal = (opportunity: Opportunity) => {
    setSelectedOpportunityForAI(opportunity)
    setIsAIModalOpen(true)
    setGeneratedResponse('')
    setAITone('professional')
    setAIStyle('helpful')

    // Auto-select project's default template if available
    if (opportunity.project_id && projects) {
      const project = projects.find((p) => p.id === opportunity.project_id)
      if (project?.default_template_id) {
        setSelectedTemplateId(project.default_template_id)
        if (isDevelopment) console.log(`🎯 Auto-selected template ${project.default_template_id} from project "${project.name}"`)
      } else {
        setSelectedTemplateId(null)
      }
    } else {
      setSelectedTemplateId(null)
    }
  }

  const handleCloseAIModal = () => {
    setIsAIModalOpen(false)
    setSelectedOpportunityForAI(null)
    setGeneratedResponse('')
    setIsGeneratingResponse(false)
    setSelectedTemplateId(null)
    setTemplateUsed(null)
    setShowSchedulingOptions(false)
    setSchedulingStrategy('optimal')
    setCustomScheduleDate('')
    setCustomScheduleTime('')
    setSchedulePreview(null)
    setGeneratedCommentId(null)
  }

  const handleLoadPromptPreview = async () => {
    if (!selectedOpportunityForAI) return

    setIsLoadingPreview(true)
    try {
      if (isDevelopment) console.log('🔍 Loading prompt preview for opportunity:', selectedOpportunityForAI.id, {
        templateId: selectedTemplateId,
        customContext
      })

      const result = await apiClient.previewPrompt(
        selectedOpportunityForAI.id,
        selectedOpportunityForAI.project_id.toString(),
        selectedTemplateId ?? undefined,
        customContext || undefined
      )

      if (result.success) {
        setPromptPreviewData(result.data)
        setShowPromptPreview(true)
        toast.success('Prompt preview loaded!')
      } else {
        throw new Error('Failed to load prompt preview')
      }
    } catch (error: any) {
      console.error('❌ Prompt preview error:', error)
      toast.error('Failed to load prompt preview')
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const handleGenerateAIResponse = async () => {
    if (!selectedOpportunityForAI) return

    // If prompt preview is enabled, show preview first
    if (enablePromptPreview && !showPromptPreview) {
      await handleLoadPromptPreview()
      return
    }

    setIsGeneratingResponse(true)
    try {
      if (isDevelopment) console.log('🤖 Generating AI response for opportunity:', selectedOpportunityForAI.id, {
        templateId: selectedTemplateId,
        customContext: customContext || undefined
      })

      // Call the real WordPress AI endpoint with optional template and custom context
      const result = await apiClient.generateAIComment(
        selectedOpportunityForAI.id,
        selectedTemplateId ?? undefined,
        customContext || undefined
      )

      if (result.success) {
        setGeneratedResponse(result.data.comment)
        setTemplateUsed(result.data.template_used || null)
        setGeneratedCommentId(result.data.comment_id || null) // Save comment ID for scheduling

        const successMessage = result.data.template_used
          ? `AI response generated using "${result.data.template_used.name}" template! (Quality: ${result.data.quality_score}/100)`
          : `AI response generated! (Quality: ${result.data.quality_score}/100, Tokens: ${result.data.tokens_used})`

        toast.success(successMessage)
        if (isDevelopment) console.log('✅ AI response generated:', {
          comment_id: result.data.comment_id,
          quality_score: result.data.quality_score,
          tokens_used: result.data.tokens_used,
          comment_length: result.data.comment.length,
          template_used: result.data.template_used
        })

        // Show scheduling options after successful generation
        setShowSchedulingOptions(true)

        // Load schedule preview for optimal strategy
        loadSchedulePreview('optimal')
      } else {
        throw new Error('AI generation failed')
      }
    } catch (error: any) {
      console.error('❌ AI generation error:', error)

      // Provide helpful error messages
      let errorMessage = 'Failed to generate AI response'
      if (error.message?.includes('OpenAI API key')) {
        errorMessage = 'OpenAI API key not configured. Please check Settings.'
      } else if (error.message?.includes('quota')) {
        errorMessage = 'OpenAI API quota exceeded. Please check your billing.'
      }

      toast.error(errorMessage)
    } finally {
      setIsGeneratingResponse(false)
    }
  }

  const handleConfirmPromptPreview = async (editedPrompt?: string, finalCustomContext?: string) => {
    setShowPromptPreview(false)

    // Update custom context if provided
    if (finalCustomContext !== undefined) {
      setCustomContext(finalCustomContext)
    }

    // TODO: If editedPrompt is provided, we need an endpoint to generate with custom prompt
    // For now, proceed with normal generation
    await handleGenerateAIResponse()
  }

  const handlePostToReddit = async () => {
    if (!selectedOpportunityForAI || !generatedResponse) return

    try {
      if (isDevelopment) console.log('📤 Posting comment to Reddit for opportunity:', selectedOpportunityForAI.id)

      // Call the real WordPress Reddit posting endpoint
      const result = await apiClient.postCommentToReddit(selectedOpportunityForAI.id, generatedResponse)

      if (result.success) {
        toast.success(`Comment posted to Reddit successfully! ${result.data?.reddit_comment_id ? `(ID: ${result.data.reddit_comment_id})` : ''}`)
        if (isDevelopment) console.log('✅ Reddit posting successful:', {
          reddit_comment_id: result.data?.reddit_comment_id,
          reddit_comment_url: result.data?.reddit_comment_url,
          message: result.message
        })

        handleCloseAIModal()
        // Refresh opportunities to update status (should now show as "contacted")
        queryClient.invalidateQueries({ queryKey: ['opportunities'] })
        queryClient.invalidateQueries({ queryKey: ['opportunity-metrics'] })
        queryClient.invalidateQueries({ queryKey: ['contacted-count'] })
      } else {
        throw new Error(result.message || 'Reddit posting failed')
      }
    } catch (error: any) {
      console.error('❌ Reddit posting error:', error)

      // Provide helpful error messages
      let errorMessage = 'Failed to post to Reddit'
      if (error.message?.includes('authentication') || error.message?.includes('token')) {
        errorMessage = 'Reddit authentication expired. Please re-authorize in Settings.'
      } else if (error.message?.includes('rate limit')) {
        errorMessage = 'Reddit rate limit exceeded. Please try again later.'
      } else if (error.message?.includes('post ID')) {
        errorMessage = 'Could not determine Reddit post ID. Please check the opportunity URL.'
      }

      toast.error(errorMessage)
    }
  }

  // ==================== SCHEDULING HANDLERS ====================

  const loadSchedulePreview = async (strategy: 'optimal' | 'immediate' | 'random') => {
    if (!selectedOpportunityForAI) return

    try {
      const preview = await apiClient.previewSchedule(selectedOpportunityForAI.id, strategy)
      setSchedulePreview(preview.data)
      if (isDevelopment) console.log('📅 Schedule preview loaded:', preview.data)
    } catch (error) {
      console.error('❌ Failed to load schedule preview:', error)
    }
  }

  const handleSchedulingStrategyChange = (strategy: 'optimal' | 'immediate' | 'custom') => {
    setSchedulingStrategy(strategy)

    if (strategy !== 'custom') {
      loadSchedulePreview(strategy)
    }
  }

  const handleSchedulePost = async () => {
    if (!selectedOpportunityForAI || !generatedCommentId) {
      toast.error('No generated comment to schedule')
      return
    }

    setIsScheduling(true)

    try {
      let customTime: number | undefined

      if (schedulingStrategy === 'custom' && customScheduleDate && customScheduleTime) {
        const dateTimeString = `${customScheduleDate}T${customScheduleTime}`
        customTime = Math.floor(new Date(dateTimeString).getTime() / 1000)
      }

      const result = await apiClient.scheduleComment({
        opportunity_id: parseInt(selectedOpportunityForAI.id),
        comment_id: generatedCommentId,
        strategy: schedulingStrategy,
        custom_time: customTime
      })

      if (result.success) {
        const timeInfo = result.data.time_until
        toast.success(`Post scheduled successfully! Will post ${timeInfo}`)
        if (isDevelopment) console.log('✅ Post scheduled:', result.data)

        handleCloseAIModal()
        queryClient.invalidateQueries({ queryKey: ['opportunities'] })
        queryClient.invalidateQueries({ queryKey: ['scheduled-activities'] })
      } else {
        throw new Error('Scheduling failed')
      }
    } catch (error: any) {
      console.error('❌ Scheduling error:', error)
      toast.error(error.message || 'Failed to schedule post')
    } finally {
      setIsScheduling(false)
    }
  }

  // Row expansion toggle
  const toggleRowExpansion = (opportunityId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(opportunityId)) {
        newSet.delete(opportunityId)
      } else {
        newSet.add(opportunityId)
      }
      return newSet
    })
  }

  // Handle canceling a scheduled post
  const handleCancelSchedule = (scheduleId: number, scheduledTime: string, opportunityTitle: string) => {
    setSelectedSchedule({ id: scheduleId, scheduledTime, opportunityTitle })
    setCancelModalOpen(true)
  }

  const confirmCancelSchedule = async () => {
    if (!selectedSchedule) return

    try {
      const response = await apiClient.cancelSchedule(selectedSchedule.id)

      if (response.success) {
        toast.success(response.message || 'Schedule cancelled successfully!')
        // Refresh opportunities to update the UI
        queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      } else {
        toast.error(response.message || 'Failed to cancel schedule')
      }
    } catch (error: any) {
      console.error('Cancel schedule error:', error)
      toast.error(error.message || 'Failed to cancel schedule')
    }
  }

  // Handle rescheduling a post
  const handleReschedule = (scheduleId: number, scheduledTime: string, opportunityTitle: string) => {
    setSelectedSchedule({ id: scheduleId, scheduledTime, opportunityTitle })
    setRescheduleModalOpen(true)
  }

  const confirmReschedule = async (newDateTime: string) => {
    if (!selectedSchedule) return

    try {
      // Send datetime string directly to avoid timezone conversion issues
      // Backend will store it as-is in the database
      const response = await apiClient.rescheduleActivity(selectedSchedule.id, newDateTime)

      if (response.success) {
        toast.success(response.message || 'Post rescheduled successfully!')
        // Refresh opportunities to update the UI
        queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      } else {
        toast.error(response.message || 'Failed to reschedule post')
      }
    } catch (error: any) {
      console.error('Reschedule error:', error)
      toast.error(error.message || 'Failed to reschedule post')
    }
  }

  // Handle posting immediately (bypassing schedule)
  const handlePostNow = (opportunity: Opportunity) => {
    if (!opportunity.ai_response?.comment_text) {
      toast.error('No comment available to post. Please generate a comment first.')
      return
    }

    setSelectedSchedule({
      id: opportunity.schedule?.id || 0,
      scheduledTime: opportunity.schedule?.scheduled_time || '',
      opportunityTitle: opportunity.title,
      opportunity
    })
    setPostNowModalOpen(true)
  }

  const confirmPostNow = async () => {
    if (!selectedSchedule?.opportunity) return

    const opportunity = selectedSchedule.opportunity

    try {
      const response = await apiClient.postCommentToReddit(
        opportunity.id,
        opportunity.ai_response!.comment_text!
      )

      if (response.success) {
        toast.success(response.message || 'Comment posted to Reddit successfully!')

        // If there was a schedule, cancel it since we posted immediately
        if (opportunity.schedule?.id) {
          await apiClient.cancelSchedule(opportunity.schedule.id)
        }

        // Refresh opportunities to update the UI
        queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      } else {
        toast.error(response.message || 'Failed to post to Reddit')
      }
    } catch (error: any) {
      console.error('Post now error:', error)
      toast.error(error.message || 'Failed to post to Reddit')
    }
  }

  // Calculate time until scheduled post
  const getTimeUntilScheduled = (scheduledTime: string): string => {
    // Parse as UTC by appending 'Z' if not already present
    const timeStr = scheduledTime.includes('Z') ? scheduledTime : scheduledTime + 'Z'
    const scheduled = new Date(timeStr)
    const now = currentTime
    const diffMs = scheduled.getTime() - now.getTime()

    if (diffMs < 0) {
      return 'Overdue'
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60))
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

    if (hours > 24) {
      const days = Math.floor(hours / 24)
      return `in ${days}d ${hours % 24}h`
    } else if (hours > 0) {
      return `in ${hours}h ${minutes}m`
    } else {
      return `in ${minutes}m`
    }
  }

  // Format scheduled time for display
  const formatScheduledTime = (scheduledTime: string): string => {
    // Parse as UTC by appending 'Z' if not already present
    const timeStr = scheduledTime.includes('Z') ? scheduledTime : scheduledTime + 'Z'
    const date = new Date(timeStr)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  // Get strategy display name
  const getStrategyName = (strategy: string): string => {
    const names: Record<string, string> = {
      'optimal': 'Optimal timing (peak hours)',
      'immediate': 'Immediate posting',
      'random': 'Random timing',
      'custom': 'Custom schedule'
    }
    return names[strategy] || strategy
  }

  // Update current time every minute for countdown
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // Update every minute

    return () => clearInterval(timer)
  }, [])

  // Priority badge styling
  const getPriorityBadge = (priority: string) => {
    const styles = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800',
    }
    return styles[priority as keyof typeof styles] || styles.low
  }

  // Status label mapping
  const getStatusLabel = (status: string) => {
    const labels = {
      new: 'NEW',
      reviewed: 'REVIEWED',
      contacted: 'CONTACTED',
      responded: 'CONTACTED', // Legacy support - should be 'contacted'
      converted: 'CONVERTED',
      archived: 'ARCHIVED',
    }
    return labels[status as keyof typeof labels] || status.toUpperCase()
  }

  // Status badge styling
  const getStatusBadge = (status: string) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      reviewed: 'bg-purple-100 text-purple-800',
      contacted: 'bg-green-100 text-green-800',
      responded: 'bg-green-100 text-green-800', // Legacy support - same as contacted
      converted: 'bg-emerald-100 text-emerald-800',
      archived: 'bg-gray-100 text-gray-800',
    }
    return styles[status as keyof typeof styles] || styles.new
  }

  // AI Status badge styling
  const getAIStatusBadge = (aiResponse?: Opportunity['ai_response']) => {
    if (!aiResponse || aiResponse.status === 'no_comment') {
      return {
        text: 'No Response',
        className: 'bg-gray-100 text-gray-600',
        icon: '❌'
      }
    }

    if (aiResponse.status === 'posted' && aiResponse.posted_at) {
      return {
        text: 'Posted',
        className: 'bg-green-100 text-green-700',
        icon: '✅'
      }
    }

    if (aiResponse.has_response) {
      return {
        text: 'Draft',
        className: 'bg-yellow-100 text-yellow-700',
        icon: '📝'
      }
    }

    return {
      text: 'No Response',
      className: 'bg-gray-100 text-gray-600',
      icon: '❌'
    }
  }

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    return `${Math.floor(diffInHours / 24)}d ago`
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">Failed to load opportunities</div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['opportunities'] })}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Opportunities</h1>
          <p className="mt-2 text-sm text-gray-600">
            View and manage Reddit opportunities discovered by your monitoring projects.
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['opportunities'] })}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <button
            onClick={handleExportCSV}
            disabled={!opportunities || opportunities.length === 0}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {/* Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-500" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Opportunities</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">{opportunityMetrics.total.toLocaleString()}</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                <span className="text-orange-600 font-bold text-sm">NEW</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">New Opportunities</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-orange-600">{opportunityMetrics.new.toLocaleString()}</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-red-600 font-bold text-sm">!</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">High Priority</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-red-600">{opportunityMetrics.high_priority.toLocaleString()}</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Contacted</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-green-600">{opportunityMetrics.contacted.toLocaleString()}</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <span className="text-emerald-600 font-bold text-sm">✓</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Converted</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-emerald-600">{opportunityMetrics.converted.toLocaleString()}</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">🤖</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">AI Generated</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-indigo-600">{opportunityMetrics.ai_generated.toLocaleString()}</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-purple-600 font-bold text-sm">📤</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">AI Posted</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-purple-600">{opportunityMetrics.ai_posted.toLocaleString()}</div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search opportunities..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Status</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="contacted">Contacted</option>
            <option value="converted">Converted</option>
            <option value="archived">Archived</option>
          </select>

          {/* AI Status Filter */}
          <select
            value={aiStatusFilter}
            onChange={(e) => handleFilterChange('ai_status', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All AI Status</option>
            <option value="no_comment">No Response</option>
            <option value="draft">Draft</option>
            <option value="posted">Posted</option>
          </select>

          {/* Priority Filter */}
          <select
            value={priorityFilter}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Priority</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {/* Project Filter */}
          <select
            value={projectFilter}
            onChange={(e) => handleFilterChange('project', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Projects</option>
            {Array.isArray(projects) ? projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            )) : null}
          </select>

          {/* Keyword Filter */}
          <select
            value={keywordFilter}
            onChange={(e) => handleFilterChange('keyword', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Keywords</option>
            {allKeywords.map((keyword) => (
              <option key={keyword} value={keyword}>
                {keyword}
              </option>
            ))}
          </select>

          {/* Clear All Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={handleClearAllFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              title="Clear all filters and search"
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedOpportunities.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800">
              {selectedOpportunities.length} opportunities selected
            </span>
            <div className="flex space-x-3">
              <button
                onClick={() => handleBulkAction('mark-reviewed')}
                className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
                disabled={bulkUpdateMutation.isPending}
              >
                Mark Reviewed
              </button>
              <button
                onClick={() => handleBulkAction('mark-contacted')}
                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                disabled={bulkUpdateMutation.isPending}
              >
                Mark Contacted
              </button>
              <button
                onClick={() => handleBulkAction('mark-converted')}
                className="px-3 py-1 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700"
                disabled={bulkUpdateMutation.isPending}
              >
                Mark Converted
              </button>
              <button
                onClick={() => handleBulkAction('mark-archived')}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
                disabled={bulkUpdateMutation.isPending}
              >
                Mark Archived
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Enhanced Opportunities Table */}
      {opportunities && opportunities.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedOpportunities.length === opportunities.length}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Post Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Metrics
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AI Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {opportunities.map((opportunity, index) => {
                  const aiStatus = getAIStatusBadge(opportunity.ai_response)

                  return (
                    <React.Fragment key={opportunity.id}>
                    <motion.tr
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50"
                    >
                      {/* Checkbox */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedOpportunities.includes(opportunity.id)}
                          onChange={() => handleOpportunitySelect(opportunity.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>

                      {/* Post Details */}
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          <a
                            href={opportunity.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline line-clamp-2"
                          >
                            {opportunity.title}
                          </a>
                          <div className="mt-1 text-xs text-gray-500">
                            <div className="flex items-center space-x-3">
                              <span>u/{opportunity.author}</span>
                              <span>•</span>
                              <span>r/{opportunity.subreddit}</span>
                              <span>•</span>
                              <span className="flex items-center">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                {formatTimeAgo(opportunity.discovered_at)}
                              </span>
                            </div>
                          </div>
                          {/* Keywords */}
                          {(() => {
                            const safeKeywords = safeExtractKeywords(opportunity.keyword_matches)
                            return safeKeywords.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {safeKeywords.slice(0, 3).map((keyword) => (
                                  <span
                                    key={keyword}
                                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                                  >
                                    {keyword}
                                  </span>
                                ))}
                                {safeKeywords.length > 3 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                    +{safeKeywords.length - 3} more
                                  </span>
                                )}
                              </div>
                            )
                          })()}
                        </div>
                      </td>

                      {/* Metrics */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center">
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 text-red-500 mr-1" />
                              <span>{opportunity.post_score || 0}</span>
                            </div>
                            <div className="flex items-center">
                              <ChatBubbleLeftRightIcon className="h-4 w-4 text-blue-500 mr-1" />
                              <span>{opportunity.num_comments || 0}</span>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Score: {opportunity.opportunity_score}%
                          </div>
                        </div>
                      </td>

                      {/* Priority */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadge(opportunity.priority)}`}>
                          {opportunity.priority}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(opportunity.status)}`}>
                          {getStatusLabel(opportunity.status)}
                        </span>
                      </td>

                      {/* AI Status */}
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${aiStatus.className}`}>
                              <span className="mr-1">{aiStatus.icon}</span>
                              {aiStatus.text}
                            </span>
                            {/* Automation Badge */}
                            {opportunity.ai_response?.auto_generated && (
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  opportunity.ai_response.status === 'pending_approval'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : opportunity.ai_response.automation_mode === 'full_auto'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}
                                title={`Auto-generated in ${opportunity.ai_response.automation_mode} mode`}
                              >
                                🤖
                                {opportunity.ai_response.status === 'pending_approval' && ' Review'}
                                {opportunity.ai_response.automation_mode === 'full_auto' && opportunity.ai_response.status !== 'pending_approval' && ' Auto'}
                              </span>
                            )}
                          </div>
                          {/* Comment Preview */}
                          {opportunity.ai_response?.comment_text && (
                            <div className="text-xs text-gray-600 italic max-w-xs">
                              <div className="bg-gray-50 rounded px-2 py-1 border-l-2 border-indigo-300">
                                "{opportunity.ai_response.comment_text.length > 100
                                  ? opportunity.ai_response.comment_text.substring(0, 100) + '...'
                                  : opportunity.ai_response.comment_text}"
                              </div>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2 justify-end">
                          {opportunity.schedule && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800" title="Scheduled">
                              📅
                            </span>
                          )}
                          <button
                            onClick={() => handleOpenAIModal(opportunity)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-indigo-600 bg-indigo-100 hover:bg-indigo-200"
                          >
                            🤖 Generate
                          </button>
                          <a
                            href={opportunity.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                          >
                            👁 View
                          </a>
                          <button
                            onClick={() => toggleRowExpansion(opportunity.id)}
                            className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                            title="Show details"
                          >
                            {expandedRows.has(opportunity.id) ? '▲' : '▼'}
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                    {expandedRows.has(opportunity.id) && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="bg-white rounded-lg border border-gray-200 p-6">
                            {/* Schedule Details Section */}
                            {opportunity.schedule ? (
                              <div className="space-y-4">
                                <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                                  <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                                    📅 Schedule Details
                                    {opportunity.schedule.status === 'scheduled' && (
                                      <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Active
                                      </span>
                                    )}
                                    {opportunity.schedule.status === 'processing' && (
                                      <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                        Processing
                                      </span>
                                    )}
                                    {opportunity.schedule.status === 'completed' && (
                                      <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        Completed
                                      </span>
                                    )}
                                    {opportunity.schedule.status === 'failed' && (
                                      <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        Failed
                                      </span>
                                    )}
                                  </h4>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div>
                                    <dl className="space-y-3">
                                      <div>
                                        <dt className="text-sm font-medium text-gray-500">Status</dt>
                                        <dd className="mt-1 text-sm text-gray-900 capitalize">{opportunity.schedule.status}</dd>
                                      </div>
                                      <div>
                                        <dt className="text-sm font-medium text-gray-500">Strategy</dt>
                                        <dd className="mt-1 text-sm text-gray-900">{getStrategyName(opportunity.schedule.timing_strategy)}</dd>
                                      </div>
                                      <div>
                                        <dt className="text-sm font-medium text-gray-500">Scheduled For</dt>
                                        <dd className="mt-1 text-sm text-gray-900 font-medium">{formatScheduledTime(opportunity.schedule.scheduled_time)}</dd>
                                      </div>
                                      {opportunity.schedule.status === 'scheduled' && (
                                        <div>
                                          <dt className="text-sm font-medium text-gray-500">Time Until Post</dt>
                                          <dd className="mt-1 text-lg font-bold text-purple-600">
                                            {getTimeUntilScheduled(opportunity.schedule.scheduled_time)}
                                          </dd>
                                        </div>
                                      )}
                                    </dl>
                                  </div>

                                  <div>
                                    <dl className="space-y-3">
                                      {opportunity.schedule.retry_count > 0 && (
                                        <div>
                                          <dt className="text-sm font-medium text-gray-500">Retry Attempts</dt>
                                          <dd className="mt-1 text-sm text-gray-900">
                                            {opportunity.schedule.retry_count} / {opportunity.schedule.max_retries}
                                          </dd>
                                        </div>
                                      )}
                                      {opportunity.schedule.error_message && (
                                        <div>
                                          <dt className="text-sm font-medium text-red-500">Error Message</dt>
                                          <dd className="mt-1 text-sm text-red-700">{opportunity.schedule.error_message}</dd>
                                        </div>
                                      )}
                                      {opportunity.schedule.executed_at && (
                                        <div>
                                          <dt className="text-sm font-medium text-gray-500">Executed At</dt>
                                          <dd className="mt-1 text-sm text-gray-900">{formatScheduledTime(opportunity.schedule.executed_at)}</dd>
                                        </div>
                                      )}
                                    </dl>
                                  </div>
                                </div>

                                {/* Comment Preview */}
                                {opportunity.ai_response?.comment_text && (
                                  <div className="mt-6 pt-4 border-t border-gray-200">
                                    <h5 className="text-sm font-medium text-gray-700 mb-2">💬 Generated Comment Preview:</h5>
                                    <div className="bg-gray-50 rounded-md p-4 text-sm text-gray-700 italic border-l-4 border-purple-500">
                                      {opportunity.ai_response.comment_text.length > 300
                                        ? opportunity.ai_response.comment_text.substring(0, 300) + '...'
                                        : opportunity.ai_response.comment_text}
                                    </div>
                                  </div>
                                )}

                                {/* Action Buttons */}
                                {opportunity.schedule.status === 'scheduled' && (
                                  <div className="mt-6 flex space-x-3 pt-4 border-t border-gray-200">
                                    <button
                                      onClick={() => handleCancelSchedule(opportunity.schedule!.id, opportunity.schedule!.scheduled_time, opportunity.title)}
                                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                    >
                                      ❌ Cancel Schedule
                                    </button>
                                    <button
                                      onClick={() => handleReschedule(opportunity.schedule!.id, opportunity.schedule!.scheduled_time, opportunity.title)}
                                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                    >
                                      🔄 Reschedule
                                    </button>
                                    <button
                                      onClick={() => handlePostNow(opportunity)}
                                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                    >
                                      📤 Post Now
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <p className="text-sm">No schedule information available for this opportunity.</p>
                                {opportunity.ai_response?.has_response && opportunity.ai_response.status === 'draft' && (
                                  <p className="text-sm mt-2">
                                    You have a draft comment. Click <strong>🤖 Generate</strong> to schedule it.
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Engagement Metrics Section - Show for ALL posted comments (scheduled or not) */}
                            {opportunity.ai_response?.status === 'posted' && opportunity.ai_response?.comment_id && (
                              <EngagementMetricsSection commentId={opportunity.ai_response.comment_id} />
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Enhanced Pagination */}
          {pagination && (
            <div ref={paginationRef} className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
                {/* Left side: Results info and per-page selector */}
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <div className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">
                      {pagination.total_count === 0 ? 0 : ((currentPage - 1) * perPage) + 1}
                    </span>
                    {' '}to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * perPage, pagination.total_count)}
                    </span>
                    {' '}of{' '}
                    <span className="font-medium">{pagination.total_count}</span>
                    {' '}opportunities
                  </div>

                  {/* Per-page selector */}
                  <div className="flex items-center space-x-2">
                    <label htmlFor="per-page" className="text-sm text-gray-700">
                      Show:
                    </label>
                    <select
                      id="per-page"
                      value={perPage}
                      onChange={(e) => handlePerPageChange(Number(e.target.value))}
                      className="border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      disabled={isLoading}
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-700">per page</span>
                  </div>

                  {/* Quick jump to page for large datasets */}
                  {pagination.total_pages > 10 && (
                    <div className="flex items-center space-x-2">
                      <label htmlFor="jump-page" className="text-sm text-gray-700">
                        Go to page:
                      </label>
                      <input
                        id="jump-page"
                        type="number"
                        min={1}
                        max={pagination.total_pages}
                        placeholder={currentPage.toString()}
                        disabled={isLoading}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const page = parseInt((e.target as HTMLInputElement).value)
                            if (page >= 1 && page <= pagination.total_pages) {
                              setCurrentPage(page)
                              ;(e.target as HTMLInputElement).value = ''
                            }
                          }
                        }}
                        className="border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 w-16"
                      />
                    </div>
                  )}
                </div>

                {/* Right side: Pagination controls */}
                {pagination.total_pages > 1 && (
                  <div className="flex items-center space-x-1">
                    {/* Mobile-first: Simple Previous/Next */}
                    <div className="flex items-center space-x-2 sm:hidden">
                      <button
                        onClick={() => {
                          if (isDevelopment) console.log('⬅️ PAGINATION DEBUG - Previous button clicked, going from page', currentPage, 'to', Math.max(1, currentPage - 1))
                          setCurrentPage(Math.max(1, currentPage - 1))
                          // Maintain scroll position after state update
                          setTimeout(scrollToPagination, 100)
                        }}
                        disabled={currentPage === 1 || isLoading}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <ChevronLeftIcon className="h-4 w-4 mr-1" />
                        )}
                        Previous
                      </button>
                      <span className="text-sm text-gray-700 px-3 py-2 bg-gray-50 rounded-lg">
                        {currentPage} of {pagination.total_pages}
                      </span>
                      <button
                        onClick={() => {
                          if (isDevelopment) console.log('➡️ PAGINATION DEBUG - Next button clicked, going from page', currentPage, 'to', Math.min(pagination.total_pages, currentPage + 1))
                          setCurrentPage(Math.min(pagination.total_pages, currentPage + 1))
                          // Maintain scroll position after state update
                          setTimeout(scrollToPagination, 100)
                        }}
                        disabled={currentPage === pagination.total_pages || isLoading}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                        {isLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4 ml-1" />
                        )}
                      </button>
                    </div>

                    {/* Desktop: Full pagination with page numbers */}
                    <div className="hidden sm:flex items-center space-x-1">
                      {/* Previous button */}
                      <button
                        onClick={() => {
                          setCurrentPage(Math.max(1, currentPage - 1))
                          setTimeout(scrollToPagination, 100)
                        }}
                        disabled={currentPage === 1 || isLoading}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <ChevronLeftIcon className="h-4 w-4 mr-1" />
                        )}
                        Previous
                      </button>

                      {/* Page numbers with ellipsis */}
                      {(() => {
                        const totalPages = pagination.total_pages
                        const maxVisiblePages = 5
                        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
                        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

                        // Adjust start page if we're near the end
                        if (endPage - startPage < maxVisiblePages - 1) {
                          startPage = Math.max(1, endPage - maxVisiblePages + 1)
                        }

                        const pages = []

                        // Add first page and ellipsis if needed
                        if (startPage > 1) {
                          pages.push(
                            <button
                              key={1}
                              onClick={() => {
                                setCurrentPage(1)
                                setTimeout(scrollToPagination, 100)
                              }}
                              disabled={isLoading}
                              className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700 transition-colors disabled:opacity-50"
                            >
                              1
                            </button>
                          )
                          if (startPage > 2) {
                            pages.push(
                              <span key="ellipsis1" className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300">
                                ⋯
                              </span>
                            )
                          }
                        }

                        // Add visible page numbers
                        for (let page = startPage; page <= endPage; page++) {
                          pages.push(
                            <button
                              key={page}
                              onClick={() => {
                                setCurrentPage(page)
                                setTimeout(scrollToPagination, 100)
                              }}
                              disabled={isLoading}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border transition-all duration-200 disabled:opacity-50 ${
                                page === currentPage
                                  ? 'z-10 bg-blue-600 border-blue-600 text-white shadow-sm'
                                  : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-400'
                              }`}
                            >
                              {page}
                            </button>
                          )
                        }

                        // Add last page and ellipsis if needed
                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span key="ellipsis2" className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300">
                                ⋯
                              </span>
                            )
                          }
                          pages.push(
                            <button
                              key={totalPages}
                              onClick={() => {
                                setCurrentPage(totalPages)
                                setTimeout(scrollToPagination, 100)
                              }}
                              disabled={isLoading}
                              className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700 transition-colors disabled:opacity-50"
                            >
                              {totalPages}
                            </button>
                          )
                        }

                        return pages
                      })()}

                      {/* Next button */}
                      <button
                        onClick={() => {
                          setCurrentPage(Math.min(pagination.total_pages, currentPage + 1))
                          setTimeout(scrollToPagination, 100)
                        }}
                        disabled={currentPage === pagination.total_pages || isLoading}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                        {isLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4 ml-1" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12 bg-white rounded-lg shadow"
        >
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No opportunities found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {debouncedSearchQuery || statusFilter || priorityFilter || projectFilter
              ? 'Try adjusting your filters to see more results.'
              : 'Start monitoring projects to discover new opportunities.'}
          </p>
        </motion.div>
      )}

      {/* AI Response Generation Modal */}
      {isAIModalOpen && selectedOpportunityForAI && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={handleCloseAIModal}>
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white" onClick={e => e.stopPropagation()}>
            <div className="mt-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Generate AI Response</h3>
                <button
                  onClick={handleCloseAIModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Opportunity Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Responding to:</h4>
                <p className="text-sm text-gray-700 mb-2">{selectedOpportunityForAI.title}</p>
                <div className="flex items-center text-xs text-gray-500 space-x-3">
                  <span>u/{selectedOpportunityForAI.author}</span>
                  <span>•</span>
                  <span>r/{selectedOpportunityForAI.subreddit}</span>
                  <span>•</span>
                  <span>Score: {selectedOpportunityForAI.opportunity_score}%</span>
                </div>
                {(() => {
                  const safeKeywords = safeExtractKeywords(selectedOpportunityForAI.keyword_matches)
                  return safeKeywords.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {safeKeywords.map((keyword) => (
                        <span
                          key={keyword}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )
                })()}
              </div>

              {/* Response Template Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Response Template <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <select
                  value={selectedTemplateId || ''}
                  onChange={(e) => setSelectedTemplateId(e.target.value ? Number(e.target.value) : null)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">🤖 No template (AI generates from scratch)</option>
                  {templatesData?.data && templatesData.data.length > 0 ? (
                    <>
                      {/* System Templates */}
                      <optgroup label="⭐ System Templates">
                        {templatesData.data
                          .filter((t) => t.user_id === 0)
                          .map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name} ({template.category})
                              {template.use_count > 0 ? ` - Used ${template.use_count}x` : ''}
                            </option>
                          ))}
                      </optgroup>
                      {/* User Templates */}
                      {templatesData.data.some((t) => t.user_id !== 0) && (
                        <optgroup label="📝 My Templates">
                          {templatesData.data
                            .filter((t) => t.user_id !== 0)
                            .map((template) => (
                              <option key={template.id} value={template.id}>
                                {template.name} ({template.category})
                                {template.use_count > 0 ? ` - Used ${template.use_count}x` : ''}
                              </option>
                            ))}
                        </optgroup>
                      )}
                    </>
                  ) : (
                    <option disabled>Loading templates...</option>
                  )}
                </select>
                {selectedTemplateId && templatesData?.data && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-md">
                    {(() => {
                      const selectedTemplate = templatesData.data.find((t) => t.id === selectedTemplateId)
                      return selectedTemplate ? (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-blue-900">
                              📋 Template Preview: {selectedTemplate.name}
                            </span>
                            <span className="text-xs text-blue-700">
                              {selectedTemplate.tone} / {selectedTemplate.style}
                            </span>
                          </div>
                          {selectedTemplate.description && (
                            <p className="text-xs text-blue-800 mb-2">{selectedTemplate.description}</p>
                          )}
                          <p className="text-xs text-blue-700 italic">
                            {selectedTemplate.template_text.length > 200
                              ? selectedTemplate.template_text.substring(0, 200) + '...'
                              : selectedTemplate.template_text}
                          </p>
                        </div>
                      ) : null
                    })()}
                  </div>
                )}
              </div>

              {/* Custom Context (Optional) */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Instructions <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <textarea
                  value={customContext}
                  onChange={(e) => setCustomContext(e.target.value)}
                  rows={3}
                  placeholder="Add any specific instructions for the AI (e.g., mention a specific feature, use certain language, etc.)..."
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Prompt Preview Checkbox */}
              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={enablePromptPreview}
                    onChange={(e) => setEnablePromptPreview(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    🔍 Preview prompt before generating <span className="text-gray-500">(see what will be sent to AI)</span>
                  </span>
                </label>
              </div>

              {/* AI Settings */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Response Tone</label>
                  <select
                    value={aiTone}
                    onChange={(e) => setAITone(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="professional">Professional</option>
                    <option value="friendly">Friendly</option>
                    <option value="casual">Casual</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Response Style</label>
                  <select
                    value={aiStyle}
                    onChange={(e) => setAIStyle(e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="helpful">Helpful</option>
                    <option value="informative">Informative</option>
                    <option value="supportive">Supportive</option>
                    <option value="advisory">Advisory</option>
                  </select>
                </div>
              </div>

              {/* Generated Response */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Generated Response</label>
                  {templateUsed && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                      📋 Based on: {templateUsed.name}
                    </span>
                  )}
                </div>
                <textarea
                  value={generatedResponse}
                  onChange={(e) => setGeneratedResponse(e.target.value)}
                  rows={8}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Click 'Generate Response' to create an AI-powered reply..."
                />
              </div>

              {/* Scheduling Options */}
              {showSchedulingOptions && generatedResponse && (
                <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    ⏰ When would you like to post this?
                  </h4>

                  <div className="space-y-3">
                    {/* Optimal Strategy */}
                    <label className="flex items-start p-3 bg-white rounded-lg border-2 border-purple-300 cursor-pointer hover:bg-purple-50 transition-colors">
                      <input
                        type="radio"
                        name="schedulingStrategy"
                        value="optimal"
                        checked={schedulingStrategy === 'optimal'}
                        onChange={() => handleSchedulingStrategyChange('optimal')}
                        className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            🎯 Optimal Time (Recommended)
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            AI-Powered
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          Post during peak activity hours for maximum engagement
                        </p>
                        {schedulingStrategy === 'optimal' && schedulePreview && (
                          <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200">
                            <div className="flex items-center text-xs">
                              <span className="font-medium text-purple-900">Suggested:</span>
                              <span className="ml-2 text-purple-800">
                                {schedulePreview.scheduled_time_formatted}
                              </span>
                              <span className="ml-2 text-purple-600">
                                ({schedulePreview.time_until})
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </label>

                    {/* Immediate Strategy */}
                    <label className="flex items-start p-3 bg-white rounded-lg border-2 border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="schedulingStrategy"
                        value="immediate"
                        checked={schedulingStrategy === 'immediate'}
                        onChange={() => handleSchedulingStrategyChange('immediate')}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            ⚡ Post Soon
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          Post within 15-30 minutes with natural timing variation
                        </p>
                        {schedulingStrategy === 'immediate' && schedulePreview && (
                          <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                            <div className="flex items-center text-xs">
                              <span className="font-medium text-blue-900">Will post:</span>
                              <span className="ml-2 text-blue-800">
                                {schedulePreview.scheduled_time_formatted}
                              </span>
                              <span className="ml-2 text-blue-600">
                                ({schedulePreview.time_until})
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </label>

                    {/* Custom Time Strategy */}
                    <label className="flex items-start p-3 bg-white rounded-lg border-2 border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="schedulingStrategy"
                        value="custom"
                        checked={schedulingStrategy === 'custom'}
                        onChange={() => handleSchedulingStrategyChange('custom')}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            📅 Custom Time
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          Choose a specific date and time to post
                        </p>
                        {schedulingStrategy === 'custom' && (
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Date
                              </label>
                              <input
                                type="date"
                                value={customScheduleDate}
                                onChange={(e) => setCustomScheduleDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Time
                              </label>
                              <input
                                type="time"
                                value={customScheduleTime}
                                onChange={(e) => setCustomScheduleTime(e.target.value)}
                                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCloseAIModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateAIResponse}
                  disabled={isGeneratingResponse || isLoadingPreview}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingPreview ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Loading Preview...</span>
                    </>
                  ) : isGeneratingResponse ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Generating...</span>
                    </>
                  ) : (
                    <>
                      {enablePromptPreview ? '🔍 Preview & Generate' : '🤖 Generate Response'}
                    </>
                  )}
                </button>
                {generatedResponse && showSchedulingOptions && (
                  <button
                    onClick={handleSchedulePost}
                    disabled={
                      isScheduling ||
                      (schedulingStrategy === 'custom' && (!customScheduleDate || !customScheduleTime))
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isScheduling ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Scheduling...</span>
                      </>
                    ) : (
                      <>⏰ Schedule Post</>
                    )}
                  </button>
                )}
                {generatedResponse && (
                  <button
                    onClick={handlePostToReddit}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    📤 Post Now
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Schedule Modal */}
      <ConfirmModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={confirmCancelSchedule}
        title="Cancel Scheduled Post"
        message={`Are you sure you want to cancel the scheduled post for:\n\n"${selectedSchedule?.opportunityTitle}"\n\nScheduled for: ${selectedSchedule?.scheduledTime ? formatScheduledTime(selectedSchedule.scheduledTime) : ''}`}
        confirmText="Yes, Cancel Schedule"
        cancelText="Keep Schedule"
        isDangerous={true}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={rescheduleModalOpen}
        onClose={() => setRescheduleModalOpen(false)}
        onConfirm={confirmReschedule}
        title={selectedSchedule?.opportunityTitle || ''}
        currentDateTime={selectedSchedule?.scheduledTime || ''}
      />

      {/* Post Now Modal */}
      <ConfirmModal
        isOpen={postNowModalOpen}
        onClose={() => setPostNowModalOpen(false)}
        onConfirm={confirmPostNow}
        title="Post to Reddit Immediately"
        message={`This will post your comment to Reddit right now, bypassing the schedule.\n\nPost: "${selectedSchedule?.opportunityTitle}"\n\nAre you sure you want to post immediately?`}
        confirmText="Yes, Post Now"
        cancelText="Cancel"
        confirmButtonClass="bg-green-600 hover:bg-green-700 focus:ring-green-500"
      />

      {/* Prompt Preview Modal */}
      {showPromptPreview && promptPreviewData && (
        <PromptPreview
          previewData={promptPreviewData}
          onClose={() => setShowPromptPreview(false)}
          onConfirm={handleConfirmPromptPreview}
          isGenerating={isGeneratingResponse}
        />
      )}
    </div>
  )
}

export default OpportunitiesPage